import * as React from 'react';
import { useCallback, useMemo } from 'react';

import { isHotkey } from 'is-hotkey';

import Toolbar from 'bundles/cml/editor/components/toolbars/Toolbar';
import { ToolButtonFactory } from 'bundles/cml/editor/components/toolbars/constants';
import FloatingMenu from 'bundles/cml/shared/components/menu/FloatingMenu';
import type { ToolbarToolsKeys, ToolsKeys } from 'bundles/cml/shared/utils/customTools';

type Props = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  isDisabled?: boolean;
  tools: ToolsKeys[];
  pageless?: boolean;
  menuDirection?: 'top' | 'bottom';
  overflowTools: ToolbarToolsKeys[];
  autoFocus: boolean;
};

const isEscapeKey = isHotkey('escape');
const isTabKey = isHotkey(['tab', 'shift+tab']);

const MoreFloatingMenu = ({ anchorEl, onClose, overflowTools, tools, autoFocus, ...props }: Props) => {
  const handleKeyDown = useCallback<(event: React.KeyboardEvent) => void>(
    ({ nativeEvent: event }) => {
      if (isEscapeKey(event) || isTabKey(event)) {
        event.preventDefault();
        anchorEl?.focus();
        onClose();
      }
    },
    [onClose, anchorEl]
  );

  const menuButtons = useMemo(
    () =>
      overflowTools.map((tool: ToolbarToolsKeys) => {
        const ToolbarButton = ToolButtonFactory[tool]();
        return <ToolbarButton key={tool} {...props} onChange={onClose} tools={tools} />;
      }),
    [overflowTools, props, onClose, tools]
  );

  return (
    <FloatingMenu
      anchorEl={anchorEl}
      pageless
      placement="bottom-end"
      enableReferenceHidden={false}
      enableEscaped={false}
      onClose={onClose}
    >
      <Toolbar onKeyDown={handleKeyDown} autoFocus={autoFocus}>
        {menuButtons}
      </Toolbar>
    </FloatingMenu>
  );
};

export default MoreFloatingMenu;
